import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Col,
  Row,
  Form,
  InputGroup,
  FormControl,
  Badge,
  Modal,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import PagingNav from "../component/PagingNav";
import { getUserSession } from "../component/userInfo";
import Image from "react-bootstrap/Image";

import "./Admin.css";

// 날짜 포맷팅 헬퍼 함수
const formatDisplayDate = (dateString) => {
  if (!dateString) return "";
  if (dateString == "0000-00-00 00:00:00") return "";
  const date = new Date(dateString);
  const today = new Date();

  if (isNaN(date.getTime())) return dateString;

  if (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  ) {
    return date.toLocaleTimeString("ko-KR");
  } else {
    return date.toLocaleDateString("ko-KR");
  }
};

const UserList = () => {
  const [paging, setPaging] = useState();
  const [list, setList] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [tempKeyword, setTempKeyword] = useState("");

  // 모달 상태 관리
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");

  useEffect(() => {
    fetchList();
  }, [keyword]);

  const fetchList = async (page) => {
    const userSession = getUserSession();
    const apiUrl = "/json/users.php";
    try {
      const formData = new FormData();
      formData.append("uid", userSession.uid);
      formData.append("action", "list");
      formData.append("perpage", "20");
      if (keyword) formData.append("keyword", keyword);
      if (page) formData.append("page", page);

      let res = await fetch(apiUrl, { method: "POST", body: formData });
      let json = await res.json();

      if (json.paging) setPaging(json.paging);
      if (json.list) setList(json.list);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const getPage = (page) => fetchList(page);
  const searchKeyword = (e) => {
    if (tempKeyword !== keyword) setKeyword(tempKeyword);
  };

  // 모달 열기 핸들러
  const handleShowModal = (user) => {
    setEditingUser(user);
    setSelectedRole(user.role);
    setShowModal(true);
  };

  // 모달 닫기 핸들러
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  // 권한 저장 핸들러
  const handleSaveRole = async () => {
    if (!editingUser) return;

    const updateApiUrl = "/json/users.php";
    const userSession = getUserSession();

    try {
      const formData = new FormData();
      formData.append("action", "updateUserRole");

      formData.append("uid", editingUser.admin_uid);
      formData.append("newRole", selectedRole);
      formData.append("changer", userSession.uid);

      const res = await fetch(updateApiUrl, { method: "POST", body: formData });
      const result = await res.json();

      if (result.success) {
        alert("권한이 성공적으로 변경되었습니다.");
        fetchList();
      } else {
        alert("권한 변경에 실패했습니다: " + result.message);
      }
    } catch (error) {
      console.error("Failed to update role:", error);
      alert("권한 변경 중 오류가 발생했습니다.");
    }
    handleCloseModal();
  };

  // 승인 처리 핸들러
  const handleApprove = async (user) => {
    if (!user) return;

    if (window.confirm(`${user.name}님을 승인하시겠습니까?`)) {
      const updateApiUrl = "/json/users.php";
      const userSession = getUserSession();

      try {
        const formData = new FormData();
        formData.append("action", "confirm");
        formData.append("uid", user.admin_uid);
        formData.append("confirm_yn", "Y"); // 승인 상태
        formData.append("changer", userSession.uid);

        const res = await fetch(updateApiUrl, {
          method: "POST",
          body: formData,
        });
        const result = await res.json();

        if (result.success) {
          alert("성공적으로 승인되었습니다.");
          fetchList(); // 목록 새로고침
        } else {
          alert("승인 처리에 실패했습니다: " + result.message);
        }
      } catch (error) {
        console.error("Failed to approve:", error);
        alert("승인 처리 중 오류가 발생했습니다.");
      }
    }
  };

  // 승인 취소 핸들러
  const handleCancelApproval = async (user) => {
    if (!user) return;

    if (window.confirm(`${user.name}님의 승인을 취소하시겠습니까?`)) {
      const updateApiUrl = "/json/users.php";
      const userSession = getUserSession();

      try {
        const formData = new FormData();
        formData.append("action", "confirm");
        formData.append("uid", user.admin_uid);
        formData.append("confirm_yn", "N"); // 승인 취소 상태
        formData.append("changer", userSession.uid);

        const res = await fetch(updateApiUrl, {
          method: "POST",
          body: formData,
        });
        const result = await res.json();

        if (result.success) {
          alert("승인이 취소되었습니다.");
          fetchList(); // 목록 새로고침
        } else {
          alert("승인 취소에 실패했습니다: " + result.message);
        }
      } catch (error) {
        console.error("Failed to cancel approval:", error);
        alert("승인 취소 중 오류가 발생했습니다.");
      }
    }
  };

  return (
    <>
      <div id="content">
        <div className="container-fluid">
          <h1 className="h3 mb-2 text-gray-800">어드민 사용자</h1>
          <div className="card shadow mb-4">
            <div className="card-body">
              <Row>
                <Col className="text-end mb-3">
                  <Link to="/UserRegForm" className="btn btn-primary">
                    신규등록
                  </Link>
                </Col>
              </Row>
              <div className="table-responsive">
                <table
                  className="table table-bordered dataTable"
                  width="100%"
                  cellSpacing="0"
                >
                  <thead>
                    <tr>
                      <th>이름</th>
                      <th>이메일</th>
                      <th>권한</th>
                      <th>승인여부</th>
                      <th>승인자</th>
                      <th>승인일</th>
                      <th>생성자</th>
                      <th>생성일</th>
                      <th>수정자</th>
                      <th>수정일</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((item) => (
                      <tr key={item.admin_no}>
                        <td>
                          <Link
                            to={`/UserForm`}
                            state={{ admin_uid: item.admin_uid }}
                          >
                            {item.name}
                          </Link>
                        </td>
                        <td>
                          <Link
                            to={`/UserForm`}
                            state={{ admin_uid: item.admin_uid }}
                          >
                            {item.email}
                          </Link>
                        </td>
                        <td>{item.role}</td>
                        <td>
                          {item.confirm_yn === "Y" ? (
                            <Badge bg="primary">승인</Badge>
                          ) : (
                            <Badge bg="warning">대기</Badge>
                          )}
                        </td>
                        <td>{item.confirm_user_name}</td>
                        <td>{formatDisplayDate(item.confirm_date)}</td>
                        <td>{item.creator_name}</td>
                        <td>{formatDisplayDate(item.idate)}</td>
                        <td>{item.changer_name}</td>
                        <td>{formatDisplayDate(item.udate)}</td>
                        <td>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleShowModal(item)}
                            className="me-1"
                          >
                            권한 변경
                          </Button>
                          {item.confirm_yn === "Y" ? (
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleCancelApproval(item)}
                            >
                              승인 취소
                            </Button>
                          ) : (
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleApprove(item)}
                            >
                              승인
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {list.length === 0 && (
                      <tr>
                        <td
                          colSpan="11"
                          className="text-center"
                          style={{ height: "10rem" }}
                        >
                          {" "}
                          <p className="mt-5">
                            <b>해당 대상자가 없습니다.</b>
                          </p>{" "}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Row>
                <Col lg={10} className="mx-auto">
                  <PagingNav paging={paging} callback={getPage} />
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </div>

      {/* 권한 변경 모달 */}
      {editingUser && (
        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>권한 변경</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              <strong>사용자:</strong> {editingUser.name}
            </p>
            <Form.Group>
              <Form.Label>권한</Form.Label>
              <Form.Select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="admin">admin</option>
                <option value="editor">editor</option>
                <option value="user">user</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              취소
            </Button>
            <Button variant="primary" onClick={handleSaveRole}>
              저장
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default UserList;
